import { keepPreviousData, useMutation, useQueries, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useMemo } from 'react'
import { match } from 'ts-pattern'
import { z, ZodSchema } from 'zod'
import {
  CreateAnalysisResponse,
  CreatePatchRequest,
  CreateScanRequestFormData,
  CreateScanResponse,
  CreateWorkflowRequest,
  Credentials,
  Finding,
  FindingTriageArticle,
  FindingTriageReport,
  HALPaginatedAzureOrganizations,
  HALPaginatedAzureProjects,
  HALPaginatedAzureRepositories,
  PaginatedAnalysisResponse,
  PaginatedChangesets,
  PaginatedFindings,
  PaginatedFixes,
  PaginatedGitlabProjects,
  PaginatedIntegrations,
  PaginatedRepositories,
  PaginatedScales,
  PaginatedWorkflows,
  Repository,
  ScanAnalysis,
  User,
} from './api-schemas.ts'
import type { paths } from './openapi-spec'

type ShapesMatch<T, U> = [T] extends [U] ? ([U] extends [T] ? true : false) : false
type TypesMatch<T, U> = ShapesMatch<T, U> extends true ? (ShapesMatch<keyof T, keyof U> extends true ? true : false) : false
type Expect<T extends true> = T

export class ApiError extends Error {
  constructor(
    public status: number,
    bodyAsText?: string | object
  ) {
    super(`API request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status

    if (bodyAsText === undefined || bodyAsText === null) {
      return
    }

    if (typeof bodyAsText === 'object') {
      this.bodyAsText = JSON.stringify(bodyAsText, undefined, 2)
      return
    }

    if (typeof bodyAsText === 'string') {
      this.bodyAsText = bodyAsText
      return
    }

    console.error(`ApiError constructor expected bodyAsText to be a string or object but was a ${typeof bodyAsText}: ${bodyAsText}`)
  }

  public bodyAsText?: string
}

export type ExpectUseGetUserSchemaIsCorrect = Expect<TypesMatch<User, paths['/api/user']['get']['responses']['200']['content']['application/json']>>

type FetchWrapperParams = {
  path: string
  options?: RequestInit
  schema: ZodSchema
  successStatusCodes?: number[]
  parseFn?: (response: Response) => any
}

/**
 * Wrapper function for API GET requests
 *
 * Makes a fetch request to the specified path, validates the response against the provided schema,
 * and handles errors by throwing an ApiError with appropriate status and body.
 *
 * @param {Object} params - Parameters for the fetch request
 * @param {string} params.path - The API endpoint path
 * @param {RequestInit} [params.options] - Optional fetch request options
 * @param {ZodSchema} params.schema - Zod schema for validating the response
 * @param {number[]} [params.successStatusCodes] - HTTP status codes considered successful (defaults to [200, 201, 204])
 * @param {Function} [params.parseFn] - Custom function to parse the response (defaults to response.json())
 * @returns {Promise<any>} - Parsed and validated response data
 * @throws {ApiError} - If the response status is not in the successStatusCodes list
 */
const fetchWrapperQuery = async ({
  path,
  options = {},
  schema,
  successStatusCodes = [200, 201, 204],
  parseFn = (response: Response) => response.json(),
}: FetchWrapperParams) => {
  const response = await fetch(path, {
    headers: {
      'X-Requested-With': 'JavaScript',
      Accept: 'application/hal+json, application/json',
    },
    ...options,
  })

  if (!successStatusCodes.includes(response.status)) {
    throw new ApiError(response.status, await response.text())
  }

  return schema.parse(await parseFn(response))
}

type FetchWrapperMutationParams = {
  path: string
  options?: RequestInit
  schema?: ZodSchema
  successStatusCodes?: number[]
  parseFn?: (response: Response) => any
}

/**
 * Wrapper function for API mutation requests (POST, PUT, DELETE, etc.)
 *
 * Makes a fetch request to the specified path with JSON content type headers,
 * validates the response against the provided schema, and handles errors.
 *
 * @param {Object} params - Parameters for the mutation request
 * @param {string} params.path - The API endpoint path
 * @param {RequestInit} [params.options] - Optional fetch request options
 * @param {ZodSchema} [params.schema] - Zod schema for validating the response (defaults to z.any())
 * @param {number[]} [params.successStatusCodes] - HTTP status codes considered successful (defaults to [200, 201, 204])
 * @param {Function} [params.parseFn] - Custom function to parse the response (defaults to returning undefined)
 * @returns {Promise<any>} - Parsed and validated response data
 * @throws {ApiError} - If the response status is not in the successStatusCodes list
 */
const fetchWrapperMutation = async ({
  path,
  options,
  schema = z.any(),
  successStatusCodes = [200, 201, 204],
  parseFn = () => undefined,
}: FetchWrapperMutationParams) => {
  const response = await fetch(path, {
    headers: {
      'X-Requested-With': 'JavaScript',
      Accept: 'application/hal+json, application/json',
      'Content-Type': 'application/json',
    },
    ...options,
  })

  if (!successStatusCodes.includes(response.status)) {
    throw new ApiError(response.status, await response.text())
  }

  return schema.parse(await parseFn(response))
}

// =============================================
// Queries
// =============================================

export const useGetUser = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: ['user'],
    queryFn: () =>
      fetchWrapperQuery({
        path: '/api/user',
        schema: User,
      }),
    enabled,
  })

export const useGetIntegrations = () => {
  return useQuery({
    queryKey: ['integrations'],
    queryFn: () =>
      fetchWrapperQuery({
        path: '/api/v1/integrations',
        schema: PaginatedIntegrations,
      }),
  })
}

export const useGetRepository = ({ path, enabled = true }: { path: string; enabled?: boolean }) => {
  return useQuery({
    queryKey: ['repository', path],
    queryFn: () =>
      fetchWrapperQuery({
        path,
        schema: Repository,
      }),
    enabled,
  })
}

export const useGetRepositories = ({ pageIndex, pageSize, searchTerm = '' }: { pageIndex: number; pageSize: number; searchTerm?: string }) => {
  let { data, ...rest } = useQuery({
    placeholderData: keepPreviousData,
    queryKey: ['repositories', pageIndex, pageSize, searchTerm],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page_number: pageIndex.toString(),
        page_size: pageSize.toString(),
        name: searchTerm,
      })

      return fetchWrapperQuery({
        path: `/api/v1/repositories?${queryParams}`,
        schema: PaginatedRepositories,
      })
    },
  })

  data = useMemo(() => data ?? { total: 0, page: { number: 0, size: 0 }, _embedded: { items: [] }, _links: { self: { href: '' } } }, [data])
  return { data, ...rest }
}

export const useGetScanAnalysis = ({ analysisId, enabled = true }: { analysisId: string; enabled?: boolean }) => {
  return useQuery({
    queryKey: ['scan-analysis', analysisId],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/analyses/${analysisId}`,
        schema: ScanAnalysis,
      }),
    enabled,
  })
}

export const useGetFindingArticle = ({ analysisId, findingId, enabled }: { analysisId: string; findingId: string; enabled: boolean }) => {
  return useQuery<string, ApiError>({
    queryKey: ['findingArticle', analysisId, findingId],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/analyses/${analysisId}/findings/${findingId}/article`,
        options: {
          headers: {
            'X-Requested-With': 'JavaScript',
            Accept: 'text/markdown',
          },
        },
        schema: FindingTriageArticle,
        successStatusCodes: [200, 204],
        parseFn: (response: Response) => response.text(),
      }),
    enabled,
    retry: 1,
  })
}

export const useGetFindingReport = ({ analysisId, findingId, enabled }: { analysisId: string; findingId: string; enabled: boolean }) => {
  return useQuery<string, ApiError>({
    queryKey: ['findingReport', analysisId, findingId],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/analyses/${analysisId}/findings/${findingId}/report`,
        options: {
          headers: {
            'X-Requested-With': 'JavaScript',
            Accept: 'text/markdown',
          },
        },
        schema: FindingTriageReport,
        successStatusCodes: [200, 204],
        parseFn: (response: Response) => response.text(),
      }),
    enabled,
    retry: 1,
  })
}

export const useGetFindingsPerAnalysis = ({ analyses }: { analyses: ScanAnalysis[] }) => {
  const pageIndex = 0
  const pageSize = 1
  return useQueries({
    queries: analyses.map(analysis => ({
      queryKey: ['findings', analysis.id, pageIndex, pageSize],
      queryFn: (
        ({ analysisId, pageIndex, pageSize }: { analysisId: string; pageIndex: number; pageSize: number }) =>
        async () => {
          const params = new URLSearchParams({
            page_number: pageIndex.toString(),
            page_size: pageSize.toString(),
          })

          return fetchWrapperQuery({
            path: `/api/v1/analyses/${analysisId}/findings?${params.toString()}`,
            schema: PaginatedFindings,
            successStatusCodes: [200],
          })
        }
      )({ analysisId: analysis.id, pageIndex, pageSize }),
    })),
  })
}

export const useGetFindings = ({
  analysisId,
  pageIndex,
  pageSize,
  hasFix,
  hasNoFix,
}: {
  analysisId: string
  pageIndex: number
  pageSize: number
  hasFix?: boolean
  hasNoFix?: boolean
}) => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: ['hal-findings', analysisId, pageIndex, pageSize, hasFix, hasNoFix],
    queryFn: (
      ({
        analysisId,
        pageIndex,
        pageSize,
        hasFix,
        hasNoFix,
      }: {
        analysisId: string
        pageIndex: number
        pageSize: number
        hasFix?: boolean
        hasNoFix?: boolean
      }) =>
      async () => {
        const params = new URLSearchParams({
          page_number: pageIndex.toString(),
          page_size: pageSize.toString(),
        })

        if (hasFix) {
          params.append('min_fix_count', '1')
        }

        if (hasNoFix) {
          params.append('max_fix_count', '0')
        }

        return fetchWrapperQuery({
          path: `/api/v1/analyses/${analysisId}/findings?${params.toString()}`,
          schema: PaginatedFindings,
          successStatusCodes: [200],
        })
      }
    )({ analysisId, pageIndex, pageSize, hasFix, hasNoFix }),
  })
}

export const useGetFixesForFinding = ({
  analysisId,
  findingId,
  pageIndex = 0,
  pageSize = 10,
  enabled,
}: {
  analysisId: string
  findingId: string
  pageIndex?: number
  pageSize?: number
  enabled: boolean
}) => {
  return useQuery({
    queryKey: ['fixesForFinding', analysisId, findingId, pageIndex, pageSize],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/analyses/${analysisId}/findings/${findingId}/fixes?page_number=${pageIndex}&page_size=${pageSize}`,
        schema: PaginatedFixes,
      }),
    enabled,
  })
}

export const useGetChangesetsForFix = ({
  fixId,
  pageIndex = 0,
  pageSize = 10,
  enabled,
}: {
  fixId: string
  pageIndex?: number
  pageSize?: number
  enabled: boolean
}) => {
  return useQuery({
    queryKey: ['changesetsForFix', fixId, pageIndex, pageSize],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/fixes/${fixId}/changesets?page_number=${pageIndex}&page_size=${pageSize}`,
        schema: PaginatedChangesets,
      }),
    enabled,
  })
}

export const useGetAnalyses = ({ repositoryId, pageIndex = 0, pageSize = 10 }: { repositoryId: string; pageIndex?: number; pageSize?: number }) => {
  return useQuery({
    queryKey: ['analyses', repositoryId, pageIndex, pageSize],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/repositories/${repositoryId}/analyses?page_number=${pageIndex}&page_size=${pageSize}`,
        schema: PaginatedAnalysisResponse,
      }),
  })
}

export const useGetAzureOrganizations = ({
  pageIndex = 0,
  pageSize = 10,
  enabled = true,
}: {
  pageIndex?: number
  pageSize?: number
  enabled?: boolean
}) => {
  let { data, ...rest } = useQuery({
    queryKey: ['azure-organizations', pageIndex, pageSize],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/integrations/azure-default/organizations?page_number=${pageIndex}&page_size=${pageSize}`,
        schema: HALPaginatedAzureOrganizations,
      }),
    enabled,
  })

  data = useMemo(() => data ?? { total: 0, page: { number: 0, size: 0 }, _embedded: { items: [] }, _links: { self: { href: '' } } }, [data])
  return { data, ...rest }
}

export const useGetAzureProjects = ({
  organizationName,
  pageIndex = 0,
  pageSize = 200,
  enabled = true,
}: {
  organizationName: string
  pageIndex?: number
  pageSize?: number
  enabled?: boolean
}) => {
  let { data, ...rest } = useQuery({
    queryKey: ['azure-projects', organizationName, pageIndex, pageSize],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/integrations/azure-default/${organizationName}/projects?page_number=${pageIndex}&page_size=${pageSize}`,
        schema: HALPaginatedAzureProjects,
      }),
    enabled: enabled && !!organizationName,
  })

  data = useMemo(() => data ?? { total: 0, page: { number: 0, size: 0 }, _embedded: { items: [] }, _links: { self: { href: '' } } }, [data])
  return { data, ...rest }
}

export const useGetAzureRepositories = ({
  organizationName,
  projectId,
  pageIndex = 0,
  pageSize = 200,
  enabled = true,
}: {
  organizationName: string
  projectId: string
  pageIndex?: number
  pageSize?: number
  enabled?: boolean
}) => {
  let { data, ...rest } = useQuery({
    queryKey: ['azure-repositories', organizationName, projectId, pageIndex, pageSize],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/integrations/azure-default/${organizationName}/projects/${projectId}/repositories?page_number=${pageIndex}&page_size=${pageSize}`,
        schema: HALPaginatedAzureRepositories,
      }),
    enabled: enabled && !!organizationName && !!projectId,
  })

  data = useMemo(() => data ?? { total: 0, page: { number: 0, size: 0 }, _embedded: { items: [] }, _links: { self: { href: '' } } }, [data])
  return { data, ...rest }
}

export const useGetAllAnalyses = ({
  states,
  pageIndex = 0,
  pageSize = 10,
  enabled = true,
}: {
  states?: Array<'queued' | 'in_progress' | 'skipped' | 'completed' | 'failed'>
  pageIndex?: number
  pageSize?: number
  enabled?: boolean
} = {}) => {
  return useQuery({
    queryKey: ['all-analyses', states, pageIndex, pageSize],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page_number: pageIndex.toString(),
        page_size: pageSize.toString(),
      })

      if (states?.length) {
        states.forEach(state => queryParams.append('states', state))
      }

      return fetchWrapperQuery({
        path: `/api/v1/analyses?${queryParams}`,
        schema: PaginatedAnalysisResponse,
      })
    },
    enabled,
  })
}

export const useGetWorkflows = ({ repositoryId, pageIndex = 0, pageSize = 100 }: { repositoryId: string; pageIndex?: number; pageSize?: number }) => {
  const queryParams = useMemo(() => {
    const params = new URLSearchParams({
      page_number: pageIndex.toString(),
      page_size: pageSize.toString(),
    })
    return params.toString()
  }, [pageIndex, pageSize])

  const { data, ...rest } = useQuery({
    queryKey: ['workflows', repositoryId, pageIndex, pageSize],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/repositories/${repositoryId}/workflows/?${queryParams}`,
        schema: PaginatedWorkflows,
      }),
  })

  return {
    data: data ?? {
      total: 0,
      page: { number: 0, size: 0 },
      _embedded: { items: [] },
      _links: { self: { href: '' } },
    },
    ...rest,
  }
}

export const useGetScales = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data, ...rest } = useQuery({
    queryKey: ['scales'],
    queryFn: () =>
      fetchWrapperQuery({
        path: '/api/v1/scales',
        schema: PaginatedScales,
      }),
    enabled,
  })

  return {
    data: data ?? {
      total: 0,
      page: { number: 0, size: 0 },
      _embedded: { items: [] },
      _links: { self: { href: '' } },
    },
    ...rest,
  }
}

export const useGetFinding = ({ analysisId, findingId, enabled = true }: { analysisId: string; findingId: string; enabled?: boolean }) => {
  return useQuery({
    queryKey: ['finding', analysisId, findingId],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/analyses/${analysisId}/findings/${findingId}`,
        schema: Finding,
      }),
    enabled,
  })
}

export const useGetGitLabProjects = ({
  pageIndex = 0,
  pageSize = 10,
  search,
  enabled = true,
}: {
  pageIndex?: number
  pageSize?: number
  search?: string
  enabled?: boolean
} = {}) => {
  const queryParams = useMemo(() => {
    const params = new URLSearchParams()
    params.set('page_number', pageIndex.toString())
    params.set('page_size', pageSize.toString())
    if (search) params.set('search', search)
    return params.toString()
  }, [pageIndex, pageSize, search])

  return useQuery({
    queryKey: ['gitlab-projects', pageIndex, pageSize, search],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/integrations/gitlab-default/projects?${queryParams}`,
        schema: PaginatedGitlabProjects,
      }),
    enabled,
    placeholderData: keepPreviousData,
  })
}

// =============================================
// Mutations
// =============================================

export type GitRepositoryParams = {
  type: 'git'
  name: string
  credentials: Credentials
  url: string
}

export type AzureRepositoryParams = {
  type: 'azure'
  // this is the organization id
  organization: string
  // this is the project name
  project: string
  // this is the repository name
  name: string
}

export type GitLabRepositoryParams = {
  type: 'gitlab'
  name: string
  namespace: string
}

type AddRepositoryParams = GitRepositoryParams | AzureRepositoryParams | GitLabRepositoryParams

const createScanFormData = (body: CreateScanRequestFormData) => {
  if (!body || !body.metadata) {
    throw new Error('Metadata is required')
  }

  const formData = new FormData()

  formData.append(
    'metadata',
    new Blob([JSON.stringify({ ...body.metadata, sha: body.metadata.sha === '' ? undefined : body.metadata.sha })], { type: 'application/json' })
  )

  if (body.files) {
    Object.entries(body.files).forEach(([_, file]) => {
      formData.append('files', file)
    })
  }

  return formData
}

export const usePostUploadScan = ({ onSuccess, onError, retry }: { onSuccess?: any; onError?: any; retry?: number } = {}) =>
  useMutation({
    mutationFn: async ({ repositoryId, body }: { repositoryId: string; body: CreateScanRequestFormData }) =>
      fetchWrapperMutation({
        path: `/api/v1/repositories/${repositoryId}/scans`,
        options: {
          method: 'POST',
          body: createScanFormData(body),
          // Override the wrapper default content type by setting the header
          // Allow the browser to handle the content type
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        schema: CreateScanResponse,
        successStatusCodes: [201],
        parseFn: (response: Response) => response.json(),
      }),
    onSuccess,
    onError,
    retry,
  })

export const usePostAnalyzeScan = ({ onSuccess, onError, retry }: { onSuccess?: any; onError?: any; retry?: number } = {}) =>
  useMutation({
    mutationFn: async ({ scanId }: { scanId: string }) =>
      fetchWrapperMutation({
        path: `/api/v1/scans/${scanId}/analyses`,
        options: {
          method: 'POST',
          // Override the wrapper default content type by setting the header
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        schema: CreateAnalysisResponse,
        successStatusCodes: [201],
        parseFn: (response: Response) => response.json(),
      }),
    onSuccess,
    onError,
    retry,
  })

export const usePostAddRepository = ({ onSuccess, onError }: { onSuccess?: any; onError?: any } = {}) =>
  useMutation({
    mutationFn: async (params: AddRepositoryParams) =>
      fetchWrapperMutation({
        path: '/api/v1/repositories',
        options: {
          method: 'POST',
          body: JSON.stringify(params),
        },
        successStatusCodes: [201],
      }),
    onSuccess,
    onError,
  })

export const usePostPatch = ({ onSuccess, onError, retry }: { onSuccess?: any; onError?: any; retry?: number } = {}) =>
  useMutation({
    mutationFn: async ({ analysisId, createPatchRequest }: { analysisId: string; createPatchRequest: CreatePatchRequest }) =>
      fetchWrapperMutation({
        path: `/api/v1/analyses/${analysisId}/patches`,
        options: {
          method: 'POST',
          body: JSON.stringify(CreatePatchRequest.parse(createPatchRequest)),
        },
        successStatusCodes: [201],
      }),
    onSuccess,
    onError,
    retry,
  })

export const usePostCreateWorkflow = ({ onSuccess, onError }: { onSuccess?: () => void; onError?: (error: any) => void } = {}) =>
  useMutation({
    mutationFn: async ({ repositoryId, workflow }: { repositoryId: string; workflow: CreateWorkflowRequest }) =>
      fetchWrapperMutation({
        path: `/api/v1/repositories/${repositoryId}/workflows`,
        options: {
          method: 'POST',
          body: JSON.stringify(CreateWorkflowRequest.parse(workflow)),
        },
        successStatusCodes: [201],
      }),
    onSuccess,
    onError,
  })

export const useDeleteWorkflow = ({ onSuccess, onError }: { onSuccess?: () => void; onError?: (error: any) => void } = {}) =>
  useMutation({
    mutationFn: async ({ workflowId }: { workflowId: string }) =>
      fetchWrapperMutation({
        path: `/api/v1/workflows/${workflowId}`,
        options: {
          method: 'DELETE',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        successStatusCodes: [204],
      }),
    onSuccess,
    onError,
  })

export const useDeleteRepository = ({ onSuccess, onError }: { onSuccess?: any; onError?: any }) =>
  useMutation({
    mutationFn: async ({ repositoryId }: { repositoryId: string }) =>
      fetchWrapperMutation({
        path: `/api/v1/repositories/${repositoryId}`,
        options: {
          method: 'DELETE',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        successStatusCodes: [204],
      }),
    onSuccess,
    onError,
    retry: 0,
  })

type LinkConfig<T> = {
  key: string // e.g. '_links.repository.href' or '_embedded.items._links.repository.href'
  schema: ZodSchema<T>
}

function useQueryWithLinks<TData, TLinks extends Record<string, any>>(
  baseQuery: UseQueryOptions<TData>,
  linkConfigs: LinkConfig<TLinks[keyof TLinks]>[]
) {
  const baseResult = useQuery(baseQuery)

  const allQueries = useMemo(() => {
    if (!baseResult.data || linkConfigs.length <= 0) return []
    return createLinksQueryConfigs(baseQuery, baseResult, linkConfigs)
  }, [baseResult.data, baseQuery.queryKey, linkConfigs])

  return { baseResult, linkedResults: useQueries({ queries: allQueries }) }
}

const createLinksQueryConfigs = <TLinks extends Record<string, any>>(
  baseQuery: UseQueryOptions<any>,
  baseResult: UseQueryResult<any>,
  linkConfigs: LinkConfig<TLinks[keyof TLinks]>[]
) =>
  linkConfigs.flatMap(config =>
    match(config.key)
      .when(
        key => key.includes('_embedded.items'),
        () => {
          const items = (baseResult.data as any)?._embedded?.items ?? []
          return items.map((item: any) =>
            createItemLinkQueryConfig(baseQuery.queryKey as string[], getPathFromData(item, config.key.replace(/^_embedded\.items\./, '')), config)
          )
        }
      )
      .otherwise(() => [createLinkQueryConfig(baseQuery.queryKey as string[], getPathFromData(baseResult.data, config.key), config)])
  )

const createItemLinkQueryConfig = <TLinks extends Record<string, any>>(baseQueryKey: string[], path: string, config: LinkConfig<any>) =>
  ({
    queryKey: [...baseQueryKey, `link:${config.key}`, `item:${path}`],
    queryFn: () => fetchWrapperQuery({ path, schema: config.schema }),
    enabled: !!path,
  }) satisfies UseQueryOptions<TLinks[keyof TLinks]>

const createLinkQueryConfig = <TLinks extends Record<string, any>>(baseQueryKey: string[], path: string, config: LinkConfig<any>) =>
  ({
    queryKey: [...baseQueryKey, `link:${config.key}`],
    queryFn: () => fetchWrapperQuery({ path, schema: config.schema }),
    enabled: !!path,
  }) satisfies UseQueryOptions<TLinks[keyof TLinks]>

const getPathFromData = (data: any, path: string): string => path.split('.').reduce((obj, key) => obj?.[key], data)

export const useGetAllAnalysesWithRepositories = ({
  states,
  pageSize,
  pageIndex,
  enabled = true,
}: {
  states: Array<'queued' | 'in_progress' | 'skipped' | 'completed' | 'failed'>
  pageSize: number
  pageIndex: number
  enabled?: boolean
}) =>
  useQueryWithLinks(
    {
      queryKey: ['all-analyses', states, pageIndex, pageSize],
      queryFn: async () => {
        const queryParams = new URLSearchParams({
          page_number: pageIndex.toString(),
          page_size: pageSize.toString(),
        })

        if (states?.length) {
          states.forEach(state => queryParams.append('states', state))
        }

        return fetchWrapperQuery({
          path: `/api/v1/analyses?${queryParams}`,
          schema: PaginatedAnalysisResponse,
        })
      },
      enabled,
    },
    [
      {
        key: '_embedded.items._embedded.scan._links.repository.href',
        schema: Repository,
      },
    ]
  )
